/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: DataOriginFeature.ts
 * Project: mle-client-n
 * Committed last: 2022/11/22 @ 166
 * ---
 * Description:
 */

import {EditorFeature} from '../common/EditorFeature';
import {Editor, StoreInitParams} from '../../observables/Editor';
import {action, computed, makeObservable, observable} from 'mobx';
import {DataRefType, DynamicDataRef, IDataRef} from '@revodigital/mpes';
import {addMissingBuiltins, containsAllBuiltins, normalizeDataRefs} from '../../../common/utils';
import {DataOrigin} from '../../../entities';
import {mleDomainGqlClient} from '../../../api/domain';
import {GET_DATAORIGINS_BY_COMPANY_ID} from '../../../api/domain/queries.gql';
import {DataRefNameFactory} from "@revodigital/mpes/lib/components/data/DataRefNameFactory";

export const DATAORG_FNAME = 'data-origin';

export class DataOriginFeature extends EditorFeature<StoreInitParams> {
    constructor(editor: Editor) {
        super(DATAORG_FNAME, editor);
        this._dataRefs = [];
        makeObservable(this);
    }

    @observable
    private _dataRefs: IDataRef[];

    @computed
    public get dataRefs() {
        return this._dataRefs;
    }

    init(data: StoreInitParams): boolean {
        this._dataRefs = data.dataRefs;

        // Normalize builtin definitions
        if (!containsAllBuiltins(this._dataRefs))
            addMissingBuiltins(this._dataRefs);
        this._dataRefs = normalizeDataRefs(this._dataRefs);

        return true;
    }

    /**
     * Aggiunge una origine dati all'etichetta corrente, controllando che non esista
     * @param dataRef Ref all'origine dati
     */
    @action
    public addDataRef(dataRef: IDataRef) {
        if (this._dataRefs.filter(it => it.name === dataRef.name).length === 0) {
            const temp = this._dataRefs.slice();
            temp.push(dataRef);
            this._dataRefs = temp;
        }
    }

    /**
     * Check if there is a data ref
     * @param type
     * @param name
     */
    public hasDataRef(type: DataRefType, name: string): boolean {
        return this._dataRefs.filter(it => it.type === type && it.name === name).length > 0;
    }

    /**
     * Get the project data origin
     * @param companyId
     */
    public async getProjectDataOrigin(companyId: number): Promise<DataOrigin | undefined> {
        const result = await mleDomainGqlClient.query({
            query: GET_DATAORIGINS_BY_COMPANY_ID,
            variables: {
                id: companyId
            },
            fetchPolicy: 'network-only'
        });

        const origins = result.data.company.dataOrigins as DataOrigin[];

        if (origins.length > 0) return origins[0];

        console.warn("No project data origin");
        return undefined;
    }

    /**
     * Connette tutti i campi dati passati se non lo sono ancora
     * @param fields Un vettore di campi dati e.g {{name}}
     * @param companyId
     */
    public async connectFields(fields: string[], companyId: number) {
        const projDT = await this.getProjectDataOrigin(
            companyId);
        const nameFactory = new DataRefNameFactory();

        if (projDT) {
            for (const field of fields)
                // Check data origin
                this.addDataRef(new DynamicDataRef(projDT.id.toString(), nameFactory.extractName(field)))
        }
    }

    /**
     * Controlla se almeno un'origine dati è collegata a mylabel
     */
    public usesMyLabel(): boolean {
        return (this._dataRefs.filter(it => it.type === DataRefType.DYNAMIC).length > 0);
    }

    destroy(): boolean {
        return true;
    }
}